<script setup>
import { ref, reactive, onMounted } from "vue";
import { apiQuery } from "@/api/j24api";
import { load } from "webfontloader";
import { all } from "axios";

const reportResult = ref({});
const tabs = reactive([]);
const selectedTab = ref("selectCustomer");
const tableData = reactive([]);
const loading = ref(false);
const selectedDetail = ref({});
const availableCustomers = ref([]);
const licenses = ref([]);
const selectedCustomer = ref("");
const maxInventoryAge = ref(5);
const maxPatchAge = ref(14);
const allApps = ref(false);

onMounted(async () => {
  loading.value = true;
  const instances = await apiQuery(
    "ELA",
    "/dashboard/patchingComplianceReport",
    { apiAction: "GetPatchInstances" }
  );
  console.log(instances);
  if (instances.length) {
    instances.forEach((instance) => {
      availableCustomers.value.push(instance.name);
    });
  }
  licenses.value = instances;
  loading.value = false;
});

async function getReportData() {
  loading.value = true;
  selectedTab.value = "Report";
  const result = await apiQuery("ELA", "/dashboard/patchingComplianceReport", {
    apiAction: "GetPatchComplianceReport",
    instance: selectedCustomer.value,
    maxInventoryAge: maxInventoryAge.value,
    complianceMaxAge: maxPatchAge.value,
    allApps: allApps.value,
  });
  // console.log(result);
  // console.log(Object.keys(result))
  processTableData(result.report);

  loading.value = false;
}

const headers = [
  { text: "Application", value: "application", sortable: true },
  { text: "Patching", value: "patching", sortable: true },
  { text: "Latest Version", value: "latestversion", sortable: true },
  { text: "Minimum Compliant Version", value: "minVersion", sortable: true },
  { text: "Release Date", value: "releaseDate", sortable: true },
  { text: "Active Devices", value: "activeDevices", sortable: true },
  { text: "Active on Min. Version", value: "countMinVersion", sortable: true },
  { text: "Compliance (%)", value: "compliancePercentage", sortable: true },
];

const detailHeaders = [
  { text: "Serial Number", value: "serialNumber", sortable: true },
  { text: "Last Updated", value: "timestamp", sortable: true },
  { text: "Version", value: "version", sortable: true },
  { text: "Compliant", value: "compliant", sortable: true },
];

function processTableData(data) {
  let selectedLicense = licenses.value.find(
    (license) => license.name == selectedCustomer.value
  );
  let subscriptions = selectedLicense.enabled_patches;

  tableData.length = 0;
  for (const key of Object.keys(data)) {
    let app = data[key];
    let row = {};
    row.application = app.application;
    //TODO: Patching Status
    row.patching = subscriptions.includes(app.id);
    if (selectedLicense.patchAll) {
      row.patching = true;
    }
    row.latestversion = app.latestVersion;
    row.minVersion = app.minimumCompliantVersion;
    row.releaseDate = app.releaseDate;
    row.activeDevices = app.devices.length;
    // count of devices where compliant is true
    row.countMinVersion = app.devices.filter(
      (device) => device.compliant
    ).length;
    // percentage of devices where compliant is true
    row.compliancePercentage =
      row.activeDevices > 0
        ? Number(((row.countMinVersion / row.activeDevices) * 100).toFixed(1))
        : 100;
    row.devices = app.devices;
    tableData.push(row);
  }
}

function clickRow(row) {
  selectedDetail.value = row;
  selectedTab.value = "Detail";
}

const downloadLoading = ref(false);
async function getXls() {
  downloadLoading.value = true;
  var data = [];
  let overview = { name: "Overview", data: [] };
  let detail = [];
  overview.data.push([
    "Application",
    "Patching",
    "Latest Version",
    "Minimum Compliant Version",
    "Release Date",
    "Active Devices",
    "Active on Min. Version",
    "Compliance (%)",
  ]);
  for (let row of tableData) {
    overview.data.push([
      row.application,
      row.patching ? "Yes" : "No",
      row.latestversion,
      row.minVersion,
      formatDate(row.releaseDate),
      row.activeDevices,
      row.countMinVersion,
      row.compliancePercentage,
    ]);
    let devices = row.devices.map((device) => {
      return [
        device.serialNumber,
        formatDate(device.timestamp),
        device.version,
        device.compliant ? "Yes" : "No",
      ];
    });
    detail.push({
      name:
        row.application.length > 31
          ? row.application.substring(0, 31)
          : row.application,
      data: [
        ["Serial Number", "Inventory Last Updated", "Version", "Compliant"],
        ...devices,
      ],
    });
  }
  data.push(overview);
  data.push(...detail);
  console.log(data);

  const result = await apiQuery("ELA", "/dashboard/patchingComplianceReport", {
    apiAction: "CreateSpreadsheet",
    data: { sheets: data },
  });

  if (!result.data) {
    console.error("No data returned from server");
    downloadLoading.value = false;
    return;
  }

  // The result is JSON with a data field that contains the base64 encoded XLSX file
  const base64Data = result.data;
  const blob = new Blob(
    [Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0))],
    {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }
  );
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "patching_compliance_report.xlsx";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  downloadLoading.value = false;
}

//function to convert iso date to local date
function formatDate(date) {
  if (date == undefined) {
    return undefined;
  }
  let text = "";
  text = new Date(date).toLocaleDateString("en-GB");
  if (text == "Invalid Date") {
    return "";
  }
  return text;
}
</script>

<template>
  <!-- <v-tabs color="#e7541e" v-model="selectedTab">
    <v-tab value="Report"></v-tab>
  </v-tabs> -->

  <v-window v-model="selectedTab">
    <v-window-item value="selectCustomer" class="pa-2">
      <v-row>
        <v-col>
          <h1>Patching Compliance Report</h1>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-select
            :loading="loading"
            v-model="selectedCustomer"
            :items="availableCustomers"
            label="Select Customer"
          ></v-select>
        </v-col>
        <v-col>
          Select the Patching License used to generate the report.
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            :loading="loading"
            v-model="maxInventoryAge"
            label="Max Inventory Age"
          ></v-text-field>
        </v-col>
        <v-col>
          Select the maximum age of the inventory data to include in the report.
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            :loading="loading"
            v-model="maxPatchAge"
            label="Max Patch Age"
          ></v-text-field>
        </v-col>
        <v-col>
          Selected the maximum number of days after a patch is released for the
          patch to be installed on a device to be considered compliant.
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-radio-group v-model="allApps">
            <v-radio
              label="Include Subscribed Apps Only"
              :value="false"
            ></v-radio>
            <v-radio label="Include All Apps" :value="true"></v-radio>
          </v-radio-group>
        </v-col>
        <v-col>
          Select whether to include all applications or only those subscribed to
          within Patch24.
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-btn @click="getReportData" :disabled="!selectedCustomer"
            >Generate Report</v-btn
          >
        </v-col>
      </v-row>
      <v-row>
        <v-alert
          >Please note that this feature is in Beta testing, please feedback any
          errors/suggestions.</v-alert
        >
      </v-row>
    </v-window-item>

    <v-window-item value="Report">
      <v-row>
        <v-spacer></v-spacer>
        <v-col cols="auto">
          <v-btn @click="getXls()" :loading="downloadLoading"
            >Download XLSX</v-btn
          >
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <EasyDataTable
            :headers="headers"
            :items="tableData"
            :loading="loading"
            theme-color="#e7541e"
            @ClickRow="clickRow"
          >
            <template #item-releaseDate="item">
              {{ formatDate(item.releaseDate) }}
            </template>
            <template #item-patching="item">
              <v-icon v-if="item.patching">mdi-check</v-icon>
              <v-icon v-else>mdi-close</v-icon>
            </template>
          </EasyDataTable>
        </v-col>
      </v-row>
    </v-window-item>

    <v-window-item value="Detail">
      <v-row>
        <v-col>
          <v-btn @click="selectedTab = 'Report'">Back</v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            label="Application"
            v-model="selectedDetail.application"
            variant="plain"
            readonly
            hide-details
          ></v-text-field>
        </v-col>
        <v-col>
          <v-text-field
            label="Latest Version"
            v-model="selectedDetail.latestversion"
            variant="plain"
            readonly
            hide-details
          ></v-text-field>
        </v-col>
        <v-col>
          <v-text-field
            label="Compliant Version"
            v-model="selectedDetail.minVersion"
            variant="plain"
            readonly
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <EasyDataTable
            v-if="selectedDetail.devices"
            :headers="detailHeaders"
            :items="selectedDetail.devices"
            theme-color="#e7541e"
          >
            <template #item-timestamp="item">
              {{ formatDate(item.timestamp) }}
            </template>
          </EasyDataTable>
        </v-col>
      </v-row>
    </v-window-item>
  </v-window>
</template>
